import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import SendIcon from '@mui/icons-material/Send';
import { useTheme, useMediaQuery } from '@mui/material';
import { ADD_CONVERSATION_MESSAGE, MARK_CONVERSATION_READ } from '../apis/graphqlQueries';
import { useAuth0 } from '@auth0/auth0-react';

const ConversationView = ({ todoId, conversation = [], responders = [], onMessageAdded }) => {
  const { user } = useAuth0();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState(conversation);
  const messagesEndRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [addMessage] = useMutation(ADD_CONVERSATION_MESSAGE);
  const [markRead] = useMutation(MARK_CONVERSATION_READ);

  // Get current user's hashed ID
  const getUserId = () => {
    if (!user || !user.sub) return null;
    return user.sub.split("|")[1];
  };

  // Hash user ID using Web Crypto API
  const hashUserId = async (userId) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(userId);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  // Determine if message is from current user
  const isMyMessage = (message) => {
    const currentUserId = getUserId();
    if (!currentUserId || !user) return false;

    // Simple comparison using email as fallback
    // The authorName should match the user's name if it's their message
    return message.authorName === user.name || message.authorUserId.includes(currentUserId);
  };

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    setMessages(conversation);
  }, [conversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Mark messages as read when conversation opens
  useEffect(() => {
    const markMessagesAsRead = async () => {
      const userId = getUserId();
      if (!userId || !todoId) return;

      try {
        await markRead({
          variables: {
            user_id: userId,
            todo_id: todoId
          }
        });
      } catch (error) {
        // Silently handle error - marking as read is not critical
      }
    };

    if (messages.length > 0) {
      markMessagesAsRead();
    }
  }, [todoId, markRead]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const userId = getUserId();
    if (!userId) {
      return;
    }

    try {
      const response = await addMessage({
        variables: {
          user_id: userId,
          todo_id: todoId,
          message: newMessage.trim()
        }
      });

      // Optimistically add message to UI
      if (response.data) {
        // Trigger parent refresh
        if (onMessageAdded) {
          onMessageAdded();
        }
      }

      setNewMessage('');
    } catch (error) {
      // Silently handle error - user can retry sending
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  if (!responders || responders.length <= 1) {
    return null; // Don't show conversation if only one participant
  }

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Conversation
      </Typography>

      {/* Messages container */}
      <Paper
        elevation={1}
        sx={{
          height: isMobile ? '300px' : '400px',
          overflow: 'auto',
          p: 2,
          mb: 2,
          backgroundColor: theme.palette.grey[50]
        }}
      >
        {messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
            No messages yet. Start the conversation!
          </Typography>
        ) : (
          messages.map((message, index) => {
            const isOwnMessage = isMyMessage(message);

            return (
              <Box
                key={message.messageId || index}
                sx={{
                  display: 'flex',
                  justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                  mb: 2
                }}
              >
                <Box
                  sx={{
                    maxWidth: isMobile ? '85%' : '70%',
                    backgroundColor: isOwnMessage
                      ? theme.palette.primary.light
                      : theme.palette.grey[200],
                    color: isOwnMessage
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    borderRadius: 2,
                    p: 1.5,
                  }}
                >
                  <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                    {message.authorName} ({message.authorRole})
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.message}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.7 }}>
                    {formatTimestamp(message.timestamp)}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Paper>

      {/* Message input */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          multiline
          maxRows={4}
          size={isMobile ? 'small' : 'medium'}
        />
        <IconButton
          color="primary"
          onClick={handleSendMessage}
          disabled={!newMessage.trim()}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&:disabled': {
              backgroundColor: theme.palette.grey[300],
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ConversationView;
